import { SVG, Box, Matrix, Point, Element as SvgElement
       } from '@svgdotjs/svg.js';

import { PositioningHints
       } from '../../collage-designer/stage/placeholder/overlays/action-bar/action-bar.component'; // eslint-disable-line
import { DragSensor } from '../image-cropper/drag-sensor';

export interface Position {
  x: number;
  y: number;
}

export interface Placement {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
}

export interface DragInfo {
  delta: Position;
  absolute: Position;
  reference: Box;
  ctm: Matrix;
}

export interface RoughBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export abstract class HandleBase {
  private sensor: DragSensor;
  private dragReference: DragInfo;

  public onRequestUpdate: (place: Placement) => void = () => {};
  public onDragEnd: () => void = () => {};
  public onClick: () => void = () => {};

  constructor(
    protected element: SvgElement, protected control: SvgElement)
  {
    this.sensor = new DragSensor(this.control);

    const transform = (p: Position, matrix: Matrix) => (
      new Point(p.x, p.y).transform(matrix) as any);

    this.sensor.onDragBegin = () => {
      const screenCTM = this.control.screenCTM();

      const centerControl =
        transform({x: this.control.cx(), y: this.control.cy()}, screenCTM);

      const centerElement =
        transform({x: this.element.cx(), y: this.element.cy()}, screenCTM);

      this.dragReference = {
        delta: this.control.point(0, 0) as any,
        absolute: {
          x: centerControl.x - centerElement.x,
          y: centerControl.y - centerElement.y,
        },
        reference: {
          ... this.element.bbox(),
          x: this.element.x() as number,
          y: this.element.y() as number,
          width: this.element.width() as number,
          height: this.element.height() as number,
        } as unknown as Box,
        ctm: screenCTM.inverse(),
      };
    };

    const relativePoint = (p: Position) => {
      const pDoc = transform(p, this.dragReference.ctm);
      const pOrigin = this.dragReference.delta as any;

      return {x: pDoc.x - pOrigin.x, y: pDoc.y - pOrigin.y};
    };

    this.sensor.onDrag = (info) => {
      const docDelta = relativePoint(info.delta);

      const place = this.drag({
        ... this.dragReference,
        delta: docDelta,
        absolute: {
          x: this.dragReference.absolute.x + info.delta.x,
          y: this.dragReference.absolute.y + info.delta.y,
        },
      });

      window.requestAnimationFrame(() => this.onRequestUpdate(place));
    };

    this.sensor.onClick = () => this.onClick();
    this.sensor.onDragEnd = () => window.requestAnimationFrame(this.onDragEnd);
  }

  public update(bounds: RoughBox): void {
    this.updateControl(bounds);
  }

  public teardown(): void {
    this.sensor.destructor();
  }

  public get actionBarPositioningHints(): PositioningHints
  {
    return {};
  }

  protected abstract updateControl(bounds: RoughBox): void;
  protected abstract drag(info: DragInfo): Placement;
  public dragEnd(): void
  { }

  protected roughCenter(element: SvgElement,
                        x: number,
                        y: number,
                        widthHint?: number,
                        heightHint?: number): void
  {
    const width = widthHint || element.width() as number;
    const height = heightHint || element.height() as number;
    element.move(x - (width / 2), y - (height / 2));
  }
}
